import { Component, computed, effect, inject, untracked } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { DATA_QAA, dataQaaPrefix } from "@firm360/libs/observability/data-qaa";
import { Button, Icon, Pill, Tooltip } from "@firm360/libs/ui/lumina";
import { of, switchMap } from "rxjs";
import { AccountService } from "../../../core";
import {
    Feature,
    FeatureService,
} from "../../../shared/feature/feature.service";
import { Firm360SharedModule } from "../../../shared/shared.module";
import { BillableItemQuery } from "../../invoicing/billable-items/state/billable-item.query";
import { OpenTimersPollerService } from "../open-timers-poller.service";
import { TimerPrefillRegistry } from "../prefill/timer-prefill";
import { TimerActionsService } from "../timer-actions.service";

@Component({
    selector: "app-header-timer",
    hostDirectives: [DATA_QAA],
    imports: [Firm360SharedModule, Button, Icon, Pill, Tooltip],
    templateUrl: "./header-timer.component.html",
    styleUrl: "./header-timer.component.scss",
    host: { "(window:focus)": "refreshIfShown()" },
})
export class HeaderTimerComponent {
    private readonly accountService = inject(AccountService);
    private readonly featureService = inject(FeatureService);
    private readonly billableItemQuery = inject(BillableItemQuery);
    private readonly openTimersPoller = inject(OpenTimersPollerService);
    private readonly prefillRegistry = inject(TimerPrefillRegistry);
    protected readonly timerActions = inject(TimerActionsService);
    protected readonly qaaPrefix = dataQaaPrefix(HeaderTimerComponent);

    private readonly account = toSignal(this.accountService.userIdentity, {
        requireSync: true,
    });

    private readonly isTimeTrackingEnabled = computed(() => {
        const account = this.account();
        return (
            !!account &&
            this.featureService.isFeatureEnabled(Feature.TIME_TRACKING, account)
        );
    });

    protected readonly showClientNumbers = computed(() => {
        const account = this.account();
        return !!account && !account.firmSettings?.hideClientNumbers;
    });

    private readonly timerUserId = computed(() =>
        this.isTimeTrackingEnabled() ? this.account()?.id : undefined,
    );

    protected readonly timers = toSignal(
        toObservable(this.timerUserId).pipe(
            switchMap((userId) =>
                userId === undefined
                    ? of(undefined)
                    : this.billableItemQuery.selectFirstByUserId(userId),
            ),
        ),
    );

    protected readonly isLoading = toSignal(
        this.billableItemQuery.selectLoading(),
        { initialValue: false },
    );

    constructor() {
        effect(() => {
            if (this.isTimeTrackingEnabled()) {
                untracked(() =>
                    this.openTimersPoller.requestLocalRefreshIfStale(),
                );
            }
        });
    }

    protected refreshIfShown(): void {
        if (this.isTimeTrackingEnabled()) {
            this.openTimersPoller.requestLocalRefreshIfStale();
        }
    }

    protected startTimer(): void {
        this.timerActions.startTimer(this.prefillRegistry.current());
    }

    protected addTime(): void {
        this.timerActions.addTime(this.prefillRegistry.current());
    }
}
